import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FingerprintIcon, ScanFaceIcon, CheckCircle2Icon } from 'lucide-react';
import { useTheme } from '../utils/theme';
/**
 * Canonical biometric authentication step — mirrors the SendMoneyScreen
 * "Scanning Fingerprint" → "Verified" sequence exactly so every transaction
 * flow shares the same auth experience.
 *
 * Sequence: scanning (2s) → verified (0.8s) → onComplete().
 * The `scanType` alternates flow-to-flow via the module-level counter,
 * matching SendMoneyScreen behaviour (face / thumb).
 */
let biometricCounter = 0;
interface BiometricScanProps {
  /** Called once the user is "verified" and the flow should advance to processing. */
  onComplete: () => void;
}
export function BiometricScan({
  onComplete
}: BiometricScanProps) {
  const {
    isDark
  } = useTheme();
  const [phase, setPhase] = useState<'scanning' | 'verified'>('scanning');
  const [scanType] = useState<'face' | 'thumb'>(() => {
    const t = biometricCounter % 2 === 0 ? 'thumb' : 'face';
    biometricCounter++;
    return t;
  });
  useEffect(() => {
    const t1 = setTimeout(() => {
      setPhase('verified');
      const t2 = setTimeout(() => onComplete(), 800);
      return () => clearTimeout(t2);
    }, 2000);
    return () => clearTimeout(t1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <motion.div key="biometric" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="w-full min-h-screen flex flex-col items-center justify-center" style={{
    background: isDark ? 'linear-gradient(180deg, rgba(26,14,8,1) 0%, rgba(45,24,16,1) 100%)' : 'linear-gradient(180deg, rgba(255,247,243,1) 0%, rgba(253,238,229,1) 100%)'
  }} data-id="element-3599" data-mp="3bsmxxtf6v4ja">
      <AnimatePresence mode="wait" data-id="element-3600" data-mp="2y0yq42bz3284">
        {phase === 'scanning' && <motion.div key="scanning" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} className="flex flex-col items-center gap-6" data-id="element-3601" data-mp="2xz46crcftg5m">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="w-24 h-24 rounded-full flex items-center justify-center" style={{
          background: 'rgba(243, 112, 36, 0.12)',
          border: '2px solid rgba(243, 112, 36, 0.3)'
        }} data-id="element-3602" data-mp="2urivyuh47fon">
              {scanType === 'thumb' ? <FingerprintIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-3603" data-mp="3sehmsol4ltem" /> : <ScanFaceIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-3604" data-mp="3nhr55wtoapr3" />}
            </motion.div>
            <div className="text-center" data-id="element-3605" data-mp="1e8jntd3lqww7">
              <p className={`text-[18px] font-bold mb-1 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3606" data-mp="107791506h1bw">
                {scanType === 'thumb' ? 'Scanning Fingerprint' : 'Scanning Face'}
              </p>
              <p className={`text-[13px] ${isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]'}`} data-id="element-3607" data-mp="1swo6wi6khafi">
                {scanType === 'thumb' ? 'Place your finger on the sensor' : 'Look at the camera'}
              </p>
            </div>
          </motion.div>}
        {phase === 'verified' && <motion.div key="verified" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} className="flex flex-col items-center gap-6" data-id="element-3608" data-mp="2bugxqm3x1twj">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center" style={{
          border: '2px solid rgba(34, 197, 94, 0.3)'
        }} data-id="element-3609" data-mp="iyg17urxct2u">
              <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-3610" data-mp="16qwl8av82v4s" />
            </motion.div>
            <div className="text-center" data-id="element-3611" data-mp="wxxwmszq74l6">
              <p className={`text-[18px] font-bold mb-1 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3612" data-mp="wwwg48zxxpw2">
                Verified Successfully
              </p>
              <p className={`text-[13px] ${isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]'}`} data-id="element-3613" data-mp="1pljxvulhdx4v">
                Processing payment...
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
interface ProcessingPaymentProps {
  /** Called after the processing delay to advance to the receipt. */
  onComplete: () => void;
  /** Optional override for the processing delay (ms). Defaults to 2500ms. */
  duration?: number;
  title?: string;
  subtitle?: string;
}
/**
 * Canonical "Processing Payment" step — mirrors the SendMoneyScreen spinner
 * screen exactly so every transaction flow shares the same processing screen.
 */
export function ProcessingPayment({
  onComplete,
  duration = 2500,
  title = 'Processing Payment',
  subtitle = 'Please wait while we process your transaction'
}: ProcessingPaymentProps) {
  const {
    isDark
  } = useTheme();
  useEffect(() => {
    const t = setTimeout(() => onComplete(), duration);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <motion.div key="processing" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className={`w-full min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-3614" data-mp="zj52ps0sekze">
      <motion.div animate={{
      rotate: 360
    }} transition={{
      duration: 1.5,
      repeat: Infinity,
      ease: 'linear'
    }} className="w-20 h-20 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024] mb-6" data-id="element-3615" data-mp="1n8h8ehwwoawy" />
      <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-3616" data-mp="1rab4ecw5oqqs">
        {title}
      </h2>
      <p className={`text-[15px] ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-3617" data-mp="1ul7zhoknbyw">
        {subtitle}
      </p>
    </motion.div>;
}